import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { LEADERSHIP, CHURCH } from "@/lib/data";

export default function LeadershipPage() {
  return (
    <main className="bg-[#050505] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pb-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/#lideranca"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#D4AF37] transition-colors mb-8"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            Voltar para Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-overline uppercase text-[#D4AF37] mb-6">
              Conheça nossa liderança
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
              Servos chamados{" "}
              <span className="italic text-white/80">para</span>{" "}
              <span className="text-[#D4AF37]">pastorear</span>.
            </h1>
            <p className="mt-6 text-white/65 max-w-2xl text-lg leading-relaxed">
              Nossa liderança é formada por homens e mulheres de fé, dedicados a servir 
              a Deus e ao povo da {CHURCH.shortName}. Cada um carrega um chamado único 
              para edificar o corpo de Cristo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* LÍDERES - Seções Individuais */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-24 sm:space-y-32">
          {LEADERSHIP.map((leader, idx) => {
            const leaderId = leader.name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-');
            return (
            <motion.article
              key={leader.name}
              id={leaderId}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                idx % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Foto */}
              <div className={`${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-[#0A0A0A]">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
              </div>

              {/* Conteúdo */}
              <div className={`${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="text-xs tracking-overline uppercase text-[#D4AF37] mb-4">
                  {leader.role}
                </p>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-4">
                  {leader.name}
                </h2>
                {leader.partner && (
                  <p className="text-lg italic text-white/65 mb-6">
                    com {leader.partner}
                  </p>
                )}
                {leader.bio ? (
                  <p className="text-white/75 leading-relaxed text-lg">
                    {leader.bio}
                  </p>
                ) : (
                  <p className="text-white/50 italic">
                    Bio em breve...
                  </p>
                )}
              </div>
            </motion.article>
            );
          })}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white mb-6">
              Quer conhecer mais sobre a {CHURCH.shortName}?
            </h2>
            <p className="text-white/65 mb-8 max-w-xl mx-auto">
              Venha fazer parte da nossa família e experimente a graça de Deus em comunidade.
            </p>
            <Link
              to="/#contato"
              className="inline-flex items-center gap-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 transition-all duration-300"
            >
              Entre em contato
              <ArrowUpRight size={16} strokeWidth={1.8} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
